import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import saveAs from 'file-saver';
import { merge } from 'lodash';
import { forkJoin, from } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CompanyFeeForm } from 'src/app/classes/form-groups/company-fee-form';
import { CompanyForm } from 'src/app/classes/form-groups/company-form';
import { LoadingMask } from 'src/app/classes/loading-mask';
import {
  ApConfig,
  ClientRegisteredVendor,
  Company
} from 'src/app/classes/models';
import { getApConfigDisplayLabel } from 'src/app/client/admin/accounting/helpers/ap-vendor-grid-actions.helper';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AddEditCompanyFeeModalComponent } from 'src/app/modals/add-edit-company-fee-modal/add-edit-company-fee-modal.component';
import {
  ApVendorClientManagedUploadModalComponent,
  ClientManagedUploadDuplicate,
  ClientManagedUploadEntry
} from 'src/app/modals/ap-vendor-client-managed-upload-modal/ap-vendor-client-managed-upload-modal.component';
import { ApVendorMapperApConfigSelectionModalComponent } from 'src/app/modals/ap-vendor-mapper-ap-config-selection-modal/ap-vendor-mapper-ap-config-selection-modal.component';
import { BlockedTaxIdsModalComponent } from 'src/app/modals/blocked-tax-ids-modal/blocked-tax-ids-modal.component';
import { ClientRegisteredVendorsUploadModalComponent } from 'src/app/modals/client-registered-vendors-upload-modal/client-registered-vendors-upload-modal.component';
import { ClientUsersUploadModalComponent } from 'src/app/modals/client-users-upload-modal/client-users-upload-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { EditClientViveStatusModalComponent } from 'src/app/modals/edit-client-vive-status-modal/edit-client-vive-status-modal.component';
import { EditFeeVouchersModalComponent } from 'src/app/modals/edit-fee-vouchers-modal/edit-fee-vouchers-modal.component';
import { ExemptVendorsUploadModalComponent } from 'src/app/modals/exempt-vendors-upload-modal/exempt-vendors-upload-modal.component';
import { FileUploadModalComponent } from 'src/app/modals/file-upload-modal/file-upload-modal.component';
import { VerifyBusinessChecksModalComponent } from 'src/app/modals/verify-business-checks-modal/verify-business-checks-modal.component';
import { AdminService } from 'src/app/services/admin.service';
import { ApVendorService } from 'src/app/services/ap-vendor.service';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { CompanyService } from 'src/app/services/company.service';
import { ReportService } from 'src/app/services/report.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { apVendorsService } from 'src/app/shared/vive-api/ap-vendors.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { companyBlocklistedTaxIdsService } from 'src/app/shared/vive-api/company-blocklisted-tax-ids.service';
import { CompanyBlocklistedTaxIdDto } from 'src/app/shared/vive-api/vive-api-models';
import {
  ApVendorsMapToExemptVendorResponseDto,
  CompaniesApVendorMetaBulkResponseDto
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

/** Distinct entries to send, and the repeats held back for display only. */
interface ParsedClientManagedCsv {
  entries: ClientManagedUploadEntry[];
  duplicates: ClientManagedUploadDuplicate[];
}

/** The one and only column the Client Managed upload template carries. */
const CLIENT_MANAGED_CSV_HEADER = 'AP ID';
/** Mirrors AP_VENDOR_META_BULK_MAX_ROWS on the API, which also enforces it. */
const CLIENT_MANAGED_MAX_ROWS = 2000;

@Component({
  selector: 'app-admin-client-admin',
  templateUrl: './admin-client-admin.component.html',
  styleUrls: ['./admin-client-admin.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminClientAdminComponent {
  private _destroyRef = inject(DestroyRef);

  constructor(
    private SharedDataService: SharedDataService,
    private CompanyService: CompanyService,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private AdminService: AdminService,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private HostElement: ElementRef,
    private ReportService: ReportService,
    private ChangeDetectorRef: ChangeDetectorRef,
    private ApVendorService: ApVendorService
  ) {
    this.SharedDataService.listenForEvent<number>().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'SetCompanyId' && typeof data === 'number') {
          this.CompanyId = data;
          Object.assign(this.suppliedQueryData, { CompanyId: this.CompanyId });
          this.loadCompanyData();
        }
      }
    });

    this.SharedDataService.sendEvent('GetCompanyId');

    this.removeUnusedFormControls();
    this.listenToSpecifiedFormControls();
  }

  CompanyId?: number;
  Company?: Company;
  CompanyForm: CompanyForm = new CompanyForm();
  adminTools?: {
    show: boolean;
    label: string;
    helperText: string;
    inputType: 'checkBox' | 'button' | 'select';
    formControl?: string;
    buttonText?: string;
    buttonFunction?: () => void;
    selectOptions?: any[];
  }[];

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'delete']
  };
  suppliedQueryData: GenericObject = {};
  reloadGrid: boolean = false;
  blockedTaxIdsCount: number = 0;
  blockedTaxIds: CompanyBlocklistedTaxIdDto[] = [];

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    const { id } = rowData;

    if (action === 'edit') {
      const companyFeeForm = new CompanyFeeForm();
      companyFeeForm.patchValue(rowData);

      this.addCompanyFee(companyFeeForm);
    }

    if (action === 'delete') {
      const modalRef = this.ModalService.open(ConfirmModalComponent, {
        centered: true,
        size: 'md'
      });
      Object.assign(modalRef.componentInstance, {
        displayText: 'Are you sure you want to delete this CompanyFee?'
      });

      modalRef.closed.subscribe({
        next: (result) => {
          if (result) {
            const query = {
              id
            };

            this.CompanyService.deleteCompanyFee(query).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully removed company fee.'
                );
                this.reloadGrid = true;
              }
            });
          }
        }
      });
    }
  }

  loadCompanyData() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot load company data. No Company ID found.',
        { returnError: true }
      );
    }

    const companyQuery = {
      excludedAttributes: ['logo'],
      aliasedAttributes: ['feeVouchersLeft', 'totalVendorsFeeWaived'],
      CompanyPackagePricing: {
        attributes: ['id', 'packageWaiverAmount']
      },
      CompanyVendor: {
        attributes: ['id'],
        feeWaived: true,
        transitionDate: null
      },
      ClientRegisteredVendorFee: {},
      CompanyFee: {}
    };

    this.CompanyService.getCompany(
      this.CompanyId,
      this.UtilityService.stringifyObjectsInQueryObject(companyQuery)
    ).subscribe({
      next: (Company) => {
        this.Company = Company;

        const { legalName } = this.Company;

        this.CompanyForm.patchValue(Company, { emitEvent: false });
        this.setAdminTools();
        this.loadBlockedTaxIds();

        this.SharedDataService.sendEvent('SetTitle', `Admin - ${legalName}`);
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  setAdminTools() {
    this.adminTools = [
      {
        show: true,
        label: 'VIVE Status',
        helperText: `${this.Company?.status}`,
        inputType: 'button',
        buttonFunction: () => this.changeViveStatus()
      },
      // Removing for now. Does not work in old app and not sure if we need in new app.
      // { label: 'Resend Vendor Invites', helperText: 'Manually resend vendor invite emails to all unpaid vendors for this client', inputType: 'button' },
      {
        show: true,
        label: 'Allow Compliance Exempt Vendors?',
        helperText:
          'Allows Compliance Exempt Vendors to be added for this Client',
        inputType: 'checkBox',
        formControl: 'allowExemptVendors'
      },
      {
        show: this.Company?.country === 'US',
        label: 'EIN/Name Validation using IRS API',
        helperText:
          'Allows use of the new Background Check Settings without enabling the Business Verified Vendors feature.',
        inputType: 'checkBox',
        formControl: 'usesNewBusinessVerification'
      },
      {
        show: true,
        label: 'Allow Business Verified or Non-verified Vendors',
        helperText:
          'Allows Client Registered Vendors to be added for this Client',
        inputType: 'checkBox',
        formControl: 'allowClientRegisteredVendors'
      },
      {
        show: this.CompanyForm.controls.allowClientRegisteredVendors.getRawValue(),
        label: 'Vet Business Verified Vendors?',
        helperText:
          'Configure Automated Business Verification for Client Registered Vendors.',
        inputType: 'button',
        buttonFunction: () => this.vetBusinessVerifiedVendors()
      },
      {
        show: true,
        label: 'Use Transporter Trades?',
        helperText:
          'Configures a Client to use Transporter Trades for Vendor Registration.',
        inputType: 'checkBox',
        formControl: 'useTransporterTrades'
      },
      {
        show: true,
        label: 'Allow Global Site Assignments?',
        helperText:
          'Allows sites to be globally assigned to vendors on API pulls',
        inputType: 'checkBox',
        formControl: 'globalAssignSites'
      },
      {
        show: true,
        label: 'Has Single Compliance?',
        helperText: 'Change between single compliance and tiered compliance.',
        inputType: 'checkBox',
        formControl: 'hasGlobalCompliance'
      },
      {
        show: true,
        label: 'Allow Client Registered Vendor Invitation Emails?',
        helperText:
          'Allows client registered vendor invitation emails to be sent.',
        inputType: 'checkBox',
        formControl: 'allowClientRegisteredVendorEmails'
      },
      {
        show: true,
        label: 'Allow Additional Endorsements',
        helperText: 'Allows GL and Auto additional endorsement settings.',
        inputType: 'checkBox',
        formControl: 'allowAdditionalInsuredEndorsements'
      },
      {
        show: true,
        label: 'Accepting New Vendors?',
        helperText:
          "When on, vendors can find this Client in 'Find a Client', and the Client can browse and invite VIVE Network vendors, including to bids. When off, the Client is hidden from 'Find a Client' and cannot browse or invite VIVE Network vendors.",
        inputType: 'checkBox',
        formControl: 'isAcceptingNewVendors'
      }
    ];
  }

  changeViveStatus() {
    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot change company status. No company record found.',
        { returnError: true }
      );
    }

    const { status } = this.Company;
    const modalRef = this.ModalService.open(
      EditClientViveStatusModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(modalRef.componentInstance, {
      status,
      Company: this.Company
    });

    modalRef.closed.subscribe({
      next: () => this.save(true)
    });
  }

  vetBusinessVerifiedVendors() {
    const modalRef = this.ModalService.open(
      VerifyBusinessChecksModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(modalRef.componentInstance, {
      CompanyForm: this.CompanyForm,
      Company: this.Company
    });

    modalRef.closed.subscribe({
      next: () => this.save(true)
    });
  }

  updateFeeVouchers() {
    const modalRef = this.ModalService.open(EditFeeVouchersModalComponent, {
      centered: true,
      size: 'md'
    });

    Object.assign(modalRef.componentInstance, {
      CompanyForm: this.CompanyForm,
      Company: this.Company
    });

    modalRef.closed.subscribe({
      next: () => {
        // Reload company data to get updated aliased attributes like feeVouchersLeft
        this.save(true, true);
      }
    });
  }

  updateBlockedList() {
    const modalRef = this.ModalService.open(BlockedTaxIdsModalComponent, {
      centered: true,
      size: 'lg'
    });

    Object.assign(modalRef.componentInstance, {
      CompanyId: this.CompanyId,
      blockedTaxIds: [...this.blockedTaxIds],
      country: this.Company?.country || 'US'
    });

    modalRef.closed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (newCount: number) => {
        this.blockedTaxIdsCount = newCount;
        this.loadBlockedTaxIds();
        this.ChangeDetectorRef.detectChanges();
      }
    });
  }

  loadBlockedTaxIds() {
    if (!this.CompanyId) {
      return;
    }

    // Admin-only screen: the blocklist modal renders each entry's Note, and
    // the API exposes that association to admin roles (VPDW-4054).
    from(
      companyBlocklistedTaxIdsService.findAllByCompanyId({
        pathParams: { companyId: this.CompanyId },
        options: {
          includeAssociations: [{ name: 'Note' }],
          requestTimeout: LOAD_BALANCER_TIMEOUT_MS
        }
      })
    )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (data) => {
          this.blockedTaxIds = data;
          this.blockedTaxIdsCount = data.length;
          this.ChangeDetectorRef.detectChanges();
        }
      });
  }

  addCompanyFee(companyFeeForm?: CompanyFeeForm) {
    const modalRef = this.ModalService.open(AddEditCompanyFeeModalComponent, {
      centered: true,
      size: 'md'
    });

    Object.assign(modalRef.componentInstance, {
      CompanyFeeForm: companyFeeForm ? companyFeeForm : new CompanyFeeForm(),
      Company: this.Company
    });

    modalRef.closed.subscribe({
      next: () => {
        if (!this.Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        this.CompanyService.saveCompany(
          this.Company,
          {},
          { manuallyHandleError: true }
        ).subscribe({
          next: (Company) => {
            Object.assign(this, { Company });
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully added Client fee.'
            );
            this.setAdminTools();
            this.reloadGrid = true;
          },
          error: (error) => {
            const { message, error: innerError } = error;
            if (innerError) {
              const { name, message } = innerError as Error;

              if (name === 'SequelizeUniqueConstraintError') {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'There was an error saving Client data.',
                  { returnError: true }
                );
              }

              throw this.ToastService.addErrorToast(
                'Error',
                message || 'There was an error saving the Client fee.',
                { returnError: true }
              );
            }

            throw this.ToastService.addErrorToast(
              'Error',
              message || 'There was an error saving the Client fee.',
              { returnError: true }
            );
          }
        });
      }
    });
  }

  uploadUsers() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload users. No company ID found.',
        { returnError: true }
      );
    }

    const modalRef = this.ModalService.open(FileUploadModalComponent, {
      backdrop: 'static',
      centered: true,
      size: 'lg'
    });
    Object.assign(modalRef.componentInstance, { title: 'Upload Users File' });

    modalRef.closed.subscribe({
      next: (Files) => {
        const usersFile = Files[0];

        this.CompanyService.processClientUsersFileUpload(
          this.CompanyId!,
          usersFile
        ).subscribe({
          next: (Users) => {
            const modalRef = this.ModalService.open(
              ClientUsersUploadModalComponent,
              { centered: true, size: 'xl' }
            );
            Object.assign(modalRef.componentInstance, {
              Users,
              Company: this.Company
            });

            modalRef.closed.subscribe({
              next: () => {
                this.reloadGrid = true;
              }
            });
          }
        });
      }
    });
  }

  convertTemplateToBlob(template: string) {
    const base64String = template;
    const arrayBuffer =
      this.UtilityService.convertBase64StringToArrayBuffer(base64String);
    const unit8Array = new Uint8Array(arrayBuffer);
    const blob = new Blob([unit8Array], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    return blob;
  }

  downloadUsersTemplate() {
    this.CompanyService.downloadCompanyUserUploadTemplate().subscribe({
      next: (clientUsersTemplateFile) => {
        saveAs(
          this.convertTemplateToBlob(clientUsersTemplateFile),
          'clientUsersUploadTemplate.xlsx'
        );
      }
    });
  }

  downloadClientRegisteredVendorUploadTemplate() {
    this.CompanyService.downloadClientRegisteredVendorUploadTemplate({
      CompanyId: this.CompanyId
    }).subscribe({
      next: (Template) => {
        saveAs(
          this.convertTemplateToBlob(Template),
          'ClientRegisteredVendorUploadTemplate.xlsx'
        );
      }
    });
  }

  uploadClientRegisteredVendorFile() {
    const modalRef = this.ModalService.open(FileUploadModalComponent, {
      centered: true,
      size: 'lg'
    });
    Object.assign(modalRef.componentInstance, {
      title: 'Upload ClientRegisteredVendors File'
    });

    modalRef.closed.subscribe({
      next: (Files) => {
        const File = Files[0];

        this.CompanyService.processClientRegisteredVendorsFileUpload(File, {
          CompanyId: this.CompanyId
        }).subscribe({
          next: (ClientRegisteredVendors) => {
            const modalRef = this.ModalService.open(
              ClientRegisteredVendorsUploadModalComponent,
              { centered: true, fullscreen: true }
            );
            Object.assign(modalRef.componentInstance, {
              ClientRegisteredVendors,
              CompanyId: this.CompanyId,
              Country: this.Company?.country
            });

            modalRef.closed.subscribe();
          }
        });
      }
    });
  }

  mapApVendorsToClientRegisteredVendors(): void {
    const modalInstance = this.ModalService.open(
      ApVendorMapperApConfigSelectionModalComponent,
      {
        centered: true,
        size: 'lg'
      }
    );

    modalInstance.componentInstance.CompanyId = this.CompanyId;

    modalInstance.closed.subscribe({
      next: ({
        selectedApConfig,
        legalNames,
        apVendorKeys
      }: {
        selectedApConfig: ApConfig;
        legalNames: string[];
        apVendorKeys: string[];
      }) => {
        if (!selectedApConfig) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot map AP Vendors to Client Registered Vendors. No AP Config selected.',
            { returnError: true }
          );
        }

        from(
          apVendorsService.mapToClientRegisteredVendor({
            pathParams: { apConfigId: selectedApConfig.id! },
            // An empty list is left out, not sent: `legalNames=` would filter
            // on a blank name rather than not at all.
            queryParams: {
              ...(legalNames?.length && { legalNames }),
              ...(apVendorKeys?.length && { apVendorKeys })
            },
            options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
          })
        ).subscribe({
          next: (clientRegisteredVendors) => {
            const modalRef = this.ModalService.open(
              ClientRegisteredVendorsUploadModalComponent,
              { centered: true, fullscreen: true }
            );

            Object.assign(modalRef.componentInstance, {
              ClientRegisteredVendors: clientRegisteredVendors,
              CompanyId: this.CompanyId,
              Country: this.Company?.country,
              selectedApConfigIds: [selectedApConfig.id!]
            });

            modalRef.closed.subscribe();
          }
        });
      }
    });
  }

  mapApVendorsToExemptVendors(): void {
    const modalInstance = this.ModalService.open(
      ApVendorMapperApConfigSelectionModalComponent,
      {
        centered: true,
        size: 'lg'
      }
    );

    modalInstance.componentInstance.CompanyId = this.CompanyId;

    modalInstance.closed.subscribe({
      next: ({
        selectedApConfig,
        legalNames,
        apVendorKeys
      }: {
        selectedApConfig: ApConfig;
        legalNames: string[];
        apVendorKeys: string[];
      }) => {
        if (!selectedApConfig) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot map AP Vendors to Compliance Exempt Vendors. No AP Config selected.',
            { returnError: true }
          );
        }

        this.ApVendorService.mapApVendorsToExemptVendors({
          apConfigId: selectedApConfig.id!,
          legalNames,
          apVendorKeys
        }).subscribe({
          next: (exemptVendors: ApVendorsMapToExemptVendorResponseDto) => {
            const modalRef = this.ModalService.open(
              ExemptVendorsUploadModalComponent,
              { centered: true, fullscreen: true }
            );
            Object.assign(modalRef.componentInstance, {
              ExemptVendors: exemptVendors,
              CompanyId: this.CompanyId
            });

            modalRef.closed.subscribe();
          }
        });
      }
    });
  }

  downloadComplianceExemptVendorTemplate() {
    this.CompanyService.downloadExemptVendorVendorUploadTemplate({
      CompanyId: this.CompanyId
    }).subscribe({
      next: (Template) => {
        saveAs(
          this.convertTemplateToBlob(Template),
          'ExemptVendorUploadTemplate.xlsx'
        );
      }
    });
  }

  uploadComplianceExemptVendorFile() {
    const modalRef = this.ModalService.open(FileUploadModalComponent, {
      centered: true,
      fullscreen: true
    });
    Object.assign(modalRef.componentInstance, {
      title: 'Upload ExemptVendors File'
    });

    modalRef.closed.subscribe({
      next: (Files) => {
        const File = Files[0];

        this.CompanyService.processExemptVendorsFileUpload(File, {
          CompanyId: this.CompanyId
        }).subscribe({
          next: (ExemptVendors) => {
            const modalRef = this.ModalService.open(
              ExemptVendorsUploadModalComponent,
              { centered: true, size: 'xl' }
            );
            Object.assign(modalRef.componentInstance, {
              ExemptVendors,
              CompanyId: this.CompanyId
            });

            modalRef.closed.subscribe();
          }
        });
      }
    });
  }

  downloadClientManagedTemplate(): void {
    // One column, built here rather than served by the API — a single header
    // needs no xlsBuilder report type. The client and AP configuration are
    // chosen in the UI, so they are deliberately not columns: staff should
    // never need to know an internal ApConfig id.
    saveAs(
      new Blob([`${CLIENT_MANAGED_CSV_HEADER}\n`], {
        type: 'text/csv;charset=utf-8'
      }),
      'ClientManagedApVendorUploadTemplate.csv'
    );
  }

  uploadClientManagedApVendorFile(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload Client Managed AP vendors. No Company ID found.',
        { returnError: true }
      );
    }

    const configModal = this.ModalService.open(
      ApVendorMapperApConfigSelectionModalComponent,
      { centered: true, size: 'lg' }
    );

    Object.assign(configModal.componentInstance, {
      CompanyId: this.CompanyId,
      showFilters: false,
      requireClientManagedSupport: true
    });

    configModal.closed.subscribe({
      next: ({ selectedApConfig }: { selectedApConfig: ApConfig }) => {
        if (!selectedApConfig?.id) {
          return;
        }

        this.promptForClientManagedFile(selectedApConfig);
      }
    });
  }

  private promptForClientManagedFile(selectedApConfig: ApConfig): void {
    const uploadModal = this.ModalService.open(FileUploadModalComponent, {
      centered: true,
      size: 'lg'
    });

    Object.assign(uploadModal.componentInstance, {
      title: 'Upload Client Managed AP Vendors File',
      helperText: `Applying to ${getApConfigDisplayLabel(selectedApConfig)}. One column, header "${CLIENT_MANAGED_CSV_HEADER}". Up to ${CLIENT_MANAGED_MAX_ROWS} rows per file.`
    });

    uploadModal.closed.subscribe({
      next: (Files) => {
        const uploadedFile = Files?.[0];

        if (!uploadedFile) {
          return;
        }

        const parsed = this.parseClientManagedCsv(uploadedFile);

        if (parsed === null) {
          return;
        }

        this.previewClientManagedUpload(
          selectedApConfig,
          uploadedFile.filename,
          parsed
        );
      }
    });
  }

  /**
   * Parse and validate the CSV in the browser. Rejecting a wrong file here
   * means no request goes out at all, and the unexpected headers can be named.
   * Returns null when the file is unusable (a toast has already been shown).
   */
  private parseClientManagedCsv(uploadedFile: {
    filename?: string;
    FileContent?: { filecontents?: string };
  }): ParsedClientManagedCsv | null {
    const dataUri = uploadedFile.FileContent?.filecontents;

    if (!dataUri) {
      this.ToastService.addErrorToast(
        'Error',
        'That file appears to be empty.'
      );
      return null;
    }

    const base64 = dataUri.slice(dataUri.indexOf(',') + 1);
    const bytes = new Uint8Array(
      this.UtilityService.convertBase64StringToArrayBuffer(base64)
    );
    const text = new TextDecoder('utf-8').decode(bytes);

    const lines = text.split(/\r\n|\r|\n/);
    const headers = this.splitCsvLine(lines[0] ?? '');
    const unexpected = headers.filter(
      (header) => header !== CLIENT_MANAGED_CSV_HEADER
    );

    if (headers.length === 0 || unexpected.length > 0) {
      this.ToastService.addErrorToast(
        "That file doesn't match the template",
        `Expected a single column headed "${CLIENT_MANAGED_CSV_HEADER}". Found ${unexpected.join(', ') || 'no header'}. Download the template and try again.`
      );
      return null;
    }

    const entries: ClientManagedUploadEntry[] = [];
    const duplicates: ClientManagedUploadDuplicate[] = [];
    // Case-insensitive, matching the collation both AP tables compare on — a
    // repeat differing only in case is still one row to the unique index.
    const firstLineByKey = new Map<string, number>();
    let dataRowCount = 0;

    // Line numbers are 1-based and include the header, so they match what the
    // person sees in their spreadsheet.
    lines.slice(1).forEach((line, index) => {
      const vendorKey = this.splitCsvLine(line)[0]?.trim() ?? '';

      if (vendorKey === '') {
        return;
      }

      dataRowCount += 1;
      const csvLine = index + 2;
      const repeatsLine = firstLineByKey.get(vendorKey.toLowerCase());

      if (repeatsLine === undefined) {
        firstLineByKey.set(vendorKey.toLowerCase(), csvLine);
        entries.push({ vendorKey, line: csvLine });
        return;
      }

      duplicates.push({ vendorKey, line: csvLine, repeatsLine });
    });

    if (dataRowCount === 0) {
      this.ToastService.addErrorToast(
        'Error',
        `That file has no ${CLIENT_MANAGED_CSV_HEADER} values in it.`
      );
      return null;
    }

    // The cap counts what the file asks for, not what survives de-duplication.
    if (dataRowCount > CLIENT_MANAGED_MAX_ROWS) {
      this.ToastService.addErrorToast(
        'That file is too large for one run',
        `It has ${dataRowCount} rows; the limit is ${CLIENT_MANAGED_MAX_ROWS}. Split it into two files and upload them one after the other.`
      );
      return null;
    }

    return { entries, duplicates };
  }

  private splitCsvLine(line: string): string[] {
    return line
      .replace(/^\uFEFF/, '')
      .split(',')
      .map((cell) =>
        cell
          .trim()
          .replace(/^"(.*)"$/, '$1')
          .trim()
      )
      .filter((cell, index, all) => index < all.length - 1 || cell !== '');
  }

  private previewClientManagedUpload(
    selectedApConfig: ApConfig,
    fileName: string | undefined,
    parsed: ParsedClientManagedCsv
  ): void {
    from(
      companiesService.bulkCreateApVendorMeta({
        pathParams: {
          companyId: this.CompanyId!,
          apConfigId: selectedApConfig.id!
        },
        data: {
          vendorKeys: parsed.entries.map(({ vendorKey }) => vendorKey),
          dryRun: true
        },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    ).subscribe({
      next: (dryRunResult: CompaniesApVendorMetaBulkResponseDto) => {
        const previewModal = this.ModalService.open(
          ApVendorClientManagedUploadModalComponent,
          { centered: true, size: 'xl' }
        );

        Object.assign(previewModal.componentInstance, {
          CompanyId: this.CompanyId,
          ApConfigId: selectedApConfig.id,
          ApConfigLabel: getApConfigDisplayLabel(selectedApConfig),
          FileName: fileName,
          Entries: parsed.entries,
          Duplicates: parsed.duplicates,
          DryRunResult: dryRunResult
        });

        previewModal.closed.subscribe();
      }
    });
  }

  downloadExemptVendorTypes() {
    this.ReportService.generateExemptVendorTypesReport({
      CompanyId: this.CompanyId
    }).subscribe({
      next: (Report: BlobPart) =>
        saveAs(new Blob([Report]), 'ExemptVendorTypes.xlsx')
    });
  }

  downloadFile(url: string) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '';
    anchor.target = '_blank';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  downloadAllFiles(
    limit: number,
    offset: number,
    filePart: number,
    filesToRetrieveObj = {}
  ) {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot download all files. No company ID found.',
        { returnError: true }
      );
    }

    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot download all files. No company record found.',
        { returnError: true }
      );
    }

    this.AdminService.getAllClientFiles(
      this.CompanyId,
      limit,
      offset,
      filePart,
      filesToRetrieveObj
    ).subscribe({
      next: (response) => {
        const {
          redirectUrl,
          isMoreFilesToDownload,
          limit,
          offset,
          filePartNumber,
          filesToRetrieveObj
        } = response;

        if (!redirectUrl) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There was an error downloading all files.',
            { returnError: true }
          );
        }

        this.downloadFile(redirectUrl);

        if (isMoreFilesToDownload) {
          this.downloadAllFiles(
            limit,
            offset,
            filePartNumber,
            filesToRetrieveObj
          );
        }

        Object.assign(this.Company!, {
          lastDateRanAllFileDownload: new Date()
        });

        this.save();
      }
    });
  }

  downloadVendorAgreementsAndW9(limit: number, offset: number) {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot download all files. No company ID found.',
        { returnError: true }
      );
    }

    this.AdminService.getAllClientVendorAgreementsAndW9s(
      this.CompanyId,
      limit,
      offset
    ).subscribe({
      next: (response) => {
        const { redirectUrl, isMoreFilesToDownload } = response;

        if (!redirectUrl) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There was an error downloading all files.',
            { returnError: true }
          );
        }

        this.downloadFile(redirectUrl);

        if (isMoreFilesToDownload) {
          this.downloadVendorAgreementsAndW9(limit, offset + limit);
        }
      }
    });
  }

  sendClientRegisteredVendorEmails() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot send client registered vendor emails. No company ID found.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Sending Invitation Emails...'
    });

    const query = {
      CompanyId: this.CompanyId,
      isClaimed: false,
      Contact: {
        options: { required: true },
        email: { $ne: null }
      }
    };

    this.ClientRegisteredVendorService.getClientRegisteredVendors(
      this.UtilityService.stringifyObjectsInQueryObject(query)
    ).subscribe({
      next: (ClientRegisteredVendors: ClientRegisteredVendor[]) => {
        if (!ClientRegisteredVendors || ClientRegisteredVendors.length === 0) {
          mask.destroy();
          throw this.ToastService.addErrorToast(
            'Error',
            'No Client Registered Vendors to send emails to.',
            { returnError: true }
          );
        }

        forkJoin(
          ClientRegisteredVendors.map((crv) =>
            this.ClientRegisteredVendorService.sendClientRegisteredVendorInvitation(
              { CompanyId: this.CompanyId, ClientRegisteredVendorId: crv.id }
            )
          )
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully sent invitational emails.'
            );
            mask.destroy();
          },
          error: () => mask.destroy()
        });
      },
      error: () => mask.destroy()
    });
  }

  listenToSpecifiedFormControls() {
    const controlsToListenTo: (keyof Company)[] = [
      'allowExemptVendors',
      'usesNewBusinessVerification',
      'allowClientRegisteredVendors',
      'useTransporterTrades',
      'globalAssignSites',
      'allowAdditionalInsuredEndorsements',
      'allowClientRegisteredVendorEmails',
      'hasGlobalCompliance',
      'isAcceptingNewVendors'
    ];

    controlsToListenTo.forEach((controlName) => {
      const control = this.CompanyForm.get(controlName);

      if (control) {
        control.valueChanges.pipe(debounceTime(400)).subscribe({
          next: (change) => {
            merge(this.Company, { [controlName]: change });
            this.setAdminTools();
            this.ChangeDetectorRef.markForCheck();
            this.save();
          }
        });
      }
    });
  }

  removeUnusedFormControls() {
    const controlsToRemove: (keyof Company)[] = [
      'StreetAddress',
      'MailingAddress',
      'logo'
    ];

    controlsToRemove.forEach((key) => this.CompanyForm.removeControl(key));
  }

  save(
    showSuccessMessage: boolean = false,
    reloadCompanyData: boolean = false
  ) {
    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save company. No company record found.',
        { returnError: true }
      );
    }

    this.CompanyService.saveCompany(this.Company).subscribe({
      next: (Company) => {
        Object.assign(this.Company!, Company);
        if (showSuccessMessage) {
          this.ToastService.addSuccessToast(
            'Success',
            'Successfully saved company.'
          );
        }

        this.setAdminTools();
        this.reloadGrid = true;
        this.ChangeDetectorRef.markForCheck();

        if (reloadCompanyData) {
          this.loadCompanyData();
        }
      }
    });
  }
}
